import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';

const FeeTypesPage = () => {
  const { t } = useTranslation();
  // Context-aware cache key
  const schoolId = typeof window !== 'undefined' ? localStorage.getItem('schoolId') : null;
  const phase = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhase') || '') : '';
  const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
  const ayId = typeof window !== 'undefined' ? (localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '') : '';
  const cacheKey = `finance:feeTypes:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;

  // Seed from cache
  const cached = (() => { try { return JSON.parse(localStorage.getItem(cacheKey) || '[]'); } catch { return []; } })();

  const [feeTypes, setFeeTypes] = useState(Array.isArray(cached) ? cached : []);
  const [loading, setLoading] = useState(true);
  const guardRef = useRef(null);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFeeType, setEditingFeeType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  

  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Removed frequency/currency/penalty configuration from Fee Types to avoid redundancy.

  // Fee Types now only maintain name and description.

  useEffect(() => {
    if ((feeTypes || []).length === 0) {
      guardRef.current = setTimeout(() => setLoading(false), 2000);
    }
    loadFeeTypes();
    return () => { if (guardRef.current) clearTimeout(guardRef.current); };
  }, []);

  const normalizeFeeType = (ft) => {
    if (!ft) return null;
    // Support multiple possible backend field names
    const id = ft.id || ft._id || `${ft.name}`;
    const name = ft.name || ft.feeName || '';
    const description = ft.description || ft.details || '';
    const createdAt = ft.createdAt || null;
    const updatedAt = ft.updatedAt || null;
    return { id, name, description, createdAt, updatedAt };
  };

  const loadFeeTypes = async () => {
    try {
      setLoading(true);
      const response = await fetchJson('/api/fee-types');
      const raw = Array.isArray(response) ? response : (response.data || response.feeTypes || []);
      const normalized = (raw || []).map(normalizeFeeType).filter(Boolean);
      setFeeTypes(normalized);
      try { localStorage.setItem(cacheKey, JSON.stringify(normalized)); } catch(_) {}
    } catch (err) {
      setError(t('feeTypesPage.errors.load'));
      console.error('Failed to load fee types:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: (formData.name || '').trim(),
        description: (formData.description || '').trim()
      };

      if (editingFeeType) {
        await fetchJson(`/api/fee-types/${editingFeeType.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        await fetchJson('/api/fee-types', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      setOpenDialog(false);
      resetForm();
      loadFeeTypes();
    } catch (err) {
      setError(err.message || t('feeTypesPage.errors.save'));
    }
  };

  const handleEdit = (feeType) => {
    setEditingFeeType(feeType);
    setFormData({
      name: feeType.name || '',
      description: feeType.description || ''
    });
    setOpenDialog(true);
  };

  const handleDelete = async (feeType) => {
    if (window.confirm(t('feeTypesPage.confirmDelete', { name: feeType.name }))) {
      try {
        await fetchJson(`/api/fee-types/${feeType.id}`, {
          method: 'DELETE'
        });
        loadFeeTypes();
      } catch (err) {
        setError(err.message || t('feeTypesPage.errors.delete'));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: ''
    });
    setEditingFeeType(null);
  };

  const filteredFeeTypes = feeTypes.filter(feeType => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = feeType.name?.toLowerCase().includes(q) || feeType.description?.toLowerCase().includes(q);
    return matchesSearch;
  });

  const columns = [
    {
      field: 'name',
      headerName: t('feeTypesPage.columns.name'),
      width: 260,
      fontWeight: 'bold'
    },
    {
      field: 'description',
      headerName: t('feeTypesPage.columns.description'),
      width: 420,
      renderCell: (p) => <Typography variant="body2" noWrap title={p.value}>{p.value}</Typography>
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('feeTypesPage.columns.actions'),
      width: 140,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon color="info" />}
          label={t('actions.edit')}
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon color="error" />}
          label={t('actions.delete')}
          onClick={() => handleDelete(params.row)}
        />
      ]
    }
  ];

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <Button
        startIcon={<AddIcon />}
        color="success"
        onClick={() => {
          resetForm();
          setOpenDialog(true);
        }}
        variant="contained"
        size="small"
      >
        {t('feeTypesPage.add')}
      </Button>
      <Button
        startIcon={<RefreshIcon />}
        color="info"
        onClick={loadFeeTypes}
        size="small"
      >
        {t('feeTypesPage.refresh')}
      </Button>
    </GridToolbarContainer>
  );

  if (loading && (feeTypes || []).length === 0) {
    return (
      <AppLoader variant="circular3" size={48} centered messages={[t('feeTypesPage.loading')]} />
    );
  }

  return (
    <Box p={2}>
      <Card>
        <CardHeader
          title={t('feeTypesPage.title')}
          subheader={t('feeTypesPage.subheader')}
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpenDialog(true);
              }}
              size="small"
            >
              {t('feeTypesPage.add')}
            </Button>
          }
        />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Search and Filter Controls */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder={t('feeTypesPage.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
          </Grid>

          {/* Data Grid */}
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredFeeTypes}
              columns={columns}
              loading={loading}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } }
              }}
              slots={{ toolbar: CustomToolbar }}
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell:hover': {
                  color: 'primary.main',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFeeType ? t('feeTypesPage.dialog.editTitle') : t('feeTypesPage.dialog.createTitle')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('feeTypesPage.dialog.fields.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('feeTypesPage.dialog.fields.description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('actions.cancel')}</Button>
          <Button onClick={handleSave} variant="contained">
            {editingFeeType ? t('feeTypesPage.dialog.actions.update') : t('feeTypesPage.dialog.actions.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeTypesPage;
